// B-72 — "Sábados laborales": the pure half of the screen where a client's worked Saturdays are
// kept (under the Cierres Calendario).
//
// The backend decides what may be saved (`mantis/src/services/cierres/openDates/`): a date must be
// a Saturday, valid and not already listed, and a past one is only a warning. The same rules are
// said here so the preview and the "Agregar" box can answer while the person types; the server
// still judges every save, and its refusal is what the screen shows when they disagree.

import { correctionError } from "./cierreCorrections";

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

const MENSAJES = {
  INVALIDA: "No es una fecha válida.",
  NO_SABADO: "No es sábado: los días de lunes a viernes ya se trabajan siempre.",
  DUPLICADA: "Ya está en la lista de sábados laborales.",
  REPETIDA: "Está repetida en lo que se va a guardar.",
  PASADA: "Ya pasó: no se programará automáticamente, el cierre de ese día se abre a mano.",
};

// A `YYYY-MM-DD` that exists, as a UTC date (no zone: a calendar day is not an instant).
function comoFecha(fecha) {
  const m = ISO.exec(String(fecha || ""));
  if (!m) return null;
  const [, anio, mes, dia] = m.map(Number);
  const d = new Date(Date.UTC(anio, mes - 1, dia));
  return d.getUTCFullYear() === anio && d.getUTCMonth() === mes - 1 && d.getUTCDate() === dia ? d : null;
}

export const esSabado = (fecha) => comoFecha(fecha)?.getUTCDay() === 6;

// "sábado 10 de octubre de 2026"
export function fechaLarga(fecha) {
  const d = comoFecha(fecha);
  if (!d) return fecha || "";
  return d.toLocaleDateString("es-CO", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
}

// Written out rather than asked of `toLocaleDateString`, whose short es-CO month ("10 de oct.")
// depends on the browser's ICU data.
const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

// "10 oct" and "2026", for the list's date column.
export function partesDeFecha(fecha) {
  const d = comoFecha(fecha);
  if (!d) return { dia: fecha || "", anio: "" };
  return { dia: `${d.getUTCDate()} ${MESES[d.getUTCMonth()]}`, anio: String(d.getUTCFullYear()) };
}

// What a date the person is about to save has against it: `{ ok, mensaje, aviso }`. `existentes`
// are the live list's dates (without the one being edited); `otras` the rest of what is being saved.
export function revisarFecha(fecha, { existentes = [], otras = [], hoy } = {}) {
  if (!comoFecha(fecha)) return { ok: false, mensaje: MENSAJES.INVALIDA, aviso: null };
  if (!esSabado(fecha)) return { ok: false, mensaje: MENSAJES.NO_SABADO, aviso: null };
  if (existentes.includes(fecha)) return { ok: false, mensaje: MENSAJES.DUPLICADA, aviso: null };
  if (otras.includes(fecha)) return { ok: false, mensaje: MENSAJES.REPETIDA, aviso: null };
  return { ok: true, mensaje: null, aviso: hoy && fecha < hoy ? MENSAJES.PASADA : null };
}

// The live list split for the screen: upcoming first (the next one on top), then the past ones,
// most recent first.
export function separarSabados(fechas, hoy) {
  const proximos = [];
  const pasados = [];
  (fechas || []).forEach((f) => (hoy && f.date < hoy ? pasados : proximos).push(f));
  proximos.sort((a, b) => a.date.localeCompare(b.date));
  pasados.sort((a, b) => b.date.localeCompare(a.date));
  return { proximos, pasados };
}

// Where a Saturday came from, in words.
export function origenDeSabado({ source, sourceFile } = {}) {
  if (source === "image") return sourceFile ? `Imagen · ${sourceFile}` : "Imagen";
  if (source === "migration") return "Configuración anterior";
  return "Manual";
}

// The preview's rows, editable: what the picture said, what it means, and whether it goes in.
// A date the server already refused starts unchecked; anything that reads as a date can still be
// corrected by hand before confirming.
export function filasDeVistaPrevia(review) {
  return (review || []).map((entrada, i) => ({
    clave: `${i}-${entrada.input ?? ""}`,
    leida: entrada.input ?? "",
    fecha: entrada.date || "",
    incluir: Boolean(entrada.ok),
  }));
}

// Each preview row judged against the live list and against the other rows being confirmed.
export function revisarVistaPrevia(filas, { existentes = [], hoy } = {}) {
  return (filas || []).map((fila, i) => {
    const otras = filas.filter((otra, j) => j < i && otra.incluir).map((otra) => otra.fecha);
    return { ...fila, ...revisarFecha(fila.fecha, { existentes, otras, hoy }) };
  });
}

// What "Guardar" sends from the preview: the checked rows that pass, in order.
export const fechasAGuardar = (revisadas) =>
  (revisadas || []).filter((fila) => fila.incluir && fila.ok).map((fila) => fila.fecha);

// What the screen says when the server refused. Its own Spanish sentence for these refusals is
// written for the operator (`openDates/index.js`); the rest go through the panel's translator.
const PROPIOS = new Set(["ACTION_FORBIDDEN", "OPEN_DATE_HAS_CLOSINGS", "INVALID_ARGUMENT", "CONFLICT", "EXTRACTION_FAILED", "EXTRACTION_UNAVAILABLE"]);

export function errorDeSabados(response) {
  if (!response) return "No se pudo conectar con el servidor. Inténtalo de nuevo.";
  if (PROPIOS.has(response.errorCode) && typeof response.message === "string" && response.message.trim()) {
    return response.message;
  }
  return correctionError(response);
}

// Why a Saturday cannot be removed or moved, said before anyone tries.
export const motivoBloqueo = (sabado) =>
  sabado?.closings
    ? `Ya tiene ${sabado.closings} cierre${sabado.closings === 1 ? "" : "s"}: no se puede quitar ni cambiar.`
    : null;

// --- Reading a picture: what the dialog shows while the AI works -----------------------------

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

const PASOS = [
  {
    paso: "file",
    titulo: "Recibir la imagen",
    detalle: (d) => (d?.name ? `${d.name}${d.bytes ? ` · ${Math.max(1, Math.round(d.bytes / 1024))} KB` : ""}` : null),
  },
  {
    paso: "read",
    titulo: "La IA lee las fechas de la imagen",
    detalle: (d, estado) => {
      if (estado === "running") return "Claude está revisando el documento; suele tardar entre 5 y 20 segundos.";
      if (estado === "failed") return "No se pudo leer la imagen.";
      return d?.found != null ? `Encontró ${plural(d.found, "fecha", "fechas")}` : null;
    },
  },
  {
    paso: "review",
    titulo: "Revisar cada fecha",
    detalle: (d) =>
      d ? `${plural(d.valid || 0, "sábado válido", "sábados válidos")}${d.refused ? ` · ${plural(d.refused, "con problemas", "con problemas")}` : ""}` : null,
  },
];

// The checklist from the progress events received so far: each step `pending`, `running`,
// `done` or `failed`, with its detail. The latest event of a step wins.
export function pasosDeLectura(eventos) {
  return PASOS.map(({ paso, titulo, detalle }) => {
    const ultimo = [...(eventos || [])].reverse().find((e) => e?.step === paso);
    const estado = ultimo?.state || "pending";
    return { paso, titulo, estado, detalle: ultimo ? detalle(ultimo.detail, estado) : null };
  });
}

export const MENSAJES_SABADOS = MENSAJES;
